import React, { useState, useEffect } from 'react';
import { projectAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Bug, Plus, Trash2, AlertTriangle, CheckCircle, Clock, XCircle, 
  Search, Filter, X, Check, User, ChevronDown, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const severityConfig = {
  critical: { color: 'bg-red-500/10 text-red-400 border-red-500/20', icon: AlertTriangle },
  high: { color: 'bg-orange-500/10 text-orange-400 border-orange-500/20', icon: AlertTriangle },
  medium: { color: 'bg-amber-500/10 text-amber-400 border-amber-500/20', icon: Clock },
  low: { color: 'bg-slate-500/10 text-slate-400 border-slate-500/20', icon: Clock },
};

const statusConfig = {
  open: { color: 'bg-red-500/10 text-red-400 border-red-500/20', label: 'Open' },
  in_progress: { color: 'bg-blue-500/10 text-blue-400 border-blue-500/20', label: 'In Progress' },
  resolved: { color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', label: 'Resolved' },
  closed: { color: 'bg-slate-500/10 text-slate-400 border-slate-500/20', label: 'Closed' },
  wont_fix: { color: 'bg-purple-500/10 text-purple-400 border-purple-500/20', label: "Won't Fix" },
};

export default function BugTrackerPanel({ projectId, members = [] }) {
  const [bugs, setBugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [expandedBug, setExpandedBug] = useState(null);

  const [newBug, setNewBug] = useState({
    title: '', description: '', severity: 'medium', status: 'open',
    assignee_id: '', steps_to_reproduce: '', expected_behavior: '', actual_behavior: '', labels: []
  });

  useEffect(() => {
    loadBugs();
  }, [projectId]);

  const loadBugs = async () => {
    try {
      const res = await projectAPI.listBugs(projectId);
      setBugs(res.data);
    } catch (err) {
      toast.error('Failed to load bugs');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBug = async () => {
    if (!newBug.title.trim()) {
      toast.error('Title is required');
      return;
    }
    try {
      await projectAPI.createBug(projectId, newBug);
      setNewBug({ title: '', description: '', severity: 'medium', status: 'open', assignee_id: '', steps_to_reproduce: '', expected_behavior: '', actual_behavior: '', labels: [] });
      setShowAddForm(false);
      loadBugs();
      toast.success('Bug reported');
    } catch (err) {
      toast.error('Failed to create bug');
    }
  };

  const handleUpdateBug = async (bugId, updates) => {
    try {
      await projectAPI.updateBug(projectId, bugId, updates);
      loadBugs();
      toast.success('Bug updated');
    } catch (err) {
      toast.error('Failed to update bug');
    }
  };

  const handleDeleteBug = async (bugId) => {
    try {
      await projectAPI.deleteBug(projectId, bugId);
      loadBugs();
      toast.success('Bug deleted');
    } catch (err) {
      toast.error('Failed to delete bug');
    }
  };

  const filteredBugs = bugs.filter(bug => {
    if (searchTerm && !bug.title.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (filterSeverity && bug.severity !== filterSeverity) return false;
    if (filterStatus && bug.status !== filterStatus) return false;
    return true;
  });

  const getMemberName = (memberId) => {
    const member = members.find(m => m.user_id === memberId);
    return member?.name || member?.email || 'Unassigned';
  };

  const bugStats = {
    open: bugs.filter(b => b.status === 'open').length,
    in_progress: bugs.filter(b => b.status === 'in_progress').length,
    resolved: bugs.filter(b => b.status === 'resolved').length,
    critical: bugs.filter(b => b.severity === 'critical' && b.status !== 'closed').length,
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin h-7 w-6 border-2 border-[var(--accent-indigo)] border-t-transparent rounded-full"></div></div>;
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-[var(--surface-border)] flex items-center gap-2 flex-wrap">
        <Badge className="bg-red-500/10 text-red-400 border-red-500/20 text-xs">
          <Bug className="w-4 h-4 mr-1" /> Bug Tracker
        </Badge>
        
        {/* Stats */}
        <div className="flex items-center gap-2 ml-2">
          <div className="flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-red-500/5 border border-red-500/10">
            <span className="opacity-50">Open:</span>
            <span className="font-medium text-red-400">{bugStats.open}</span>
          </div>
          <div className="flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-blue-500/5 border border-blue-500/10">
            <span className="opacity-50">In Progress:</span>
            <span className="font-medium text-blue-400">{bugStats.in_progress}</span>
          </div>
          {bugStats.critical > 0 && (
            <div className="flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-red-500/10 border border-red-500/20">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              <span className="font-medium text-red-400">{bugStats.critical} Critical</span>
            </div>
          )}
        </div>
        
        <div className="flex-1" />
        
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 opacity-40" />
          <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search..." className="h-8 text-xs pl-7 w-36 bg-transparent border-[var(--surface-border)]" data-testid="bug-search" />
        </div>
        <Select value={filterSeverity || 'all'} onValueChange={(v) => setFilterSeverity(v === 'all' ? '' : v)}>
          <SelectTrigger className="h-8 w-28 text-xs bg-transparent border-[var(--surface-border)]" data-testid="filter-severity">
            <Filter className="w-4 h-4 mr-1" /><SelectValue placeholder="Severity" />
          </SelectTrigger>
          <SelectContent className="bg-[var(--surface-bg)] border-[var(--surface-border)]">
            <SelectItem value="all" className="text-xs">All</SelectItem>
            <SelectItem value="critical" className="text-xs">Critical</SelectItem>
            <SelectItem value="high" className="text-xs">High</SelectItem>
            <SelectItem value="medium" className="text-xs">Medium</SelectItem>
            <SelectItem value="low" className="text-xs">Low</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterStatus || 'all'} onValueChange={(v) => setFilterStatus(v === 'all' ? '' : v)}>
          <SelectTrigger className="h-8 w-28 text-xs bg-transparent border-[var(--surface-border)]" data-testid="filter-bug-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-[var(--surface-bg)] border-[var(--surface-border)]">
            <SelectItem value="all" className="text-xs">All</SelectItem>
            {Object.entries(statusConfig).map(([k, v]) => <SelectItem key={k} value={k} className="text-xs">{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" className="h-8 text-xs bg-red-500 hover:bg-red-600 text-white" onClick={() => setShowAddForm(true)} data-testid="add-bug-btn">
          <Plus className="w-4 h-4 mr-1" /> Report Bug
        </Button>
      </div>

      {/* Add Bug Form */}
      {showAddForm && (
        <div className="p-3 border-b border-[var(--surface-border)] bg-[var(--surface-card)]">
          <div className="grid grid-cols-2 gap-3 max-w-2xl">
            <div className="col-span-2">
              <Label className="text-xs mb-1 block opacity-60">Bug Title *</Label>
              <Input value={newBug.title} onChange={(e) => setNewBug(b => ({ ...b, title: e.target.value }))} placeholder="Brief description of the bug" className="h-8 text-sm bg-transparent border-[var(--surface-border)]" data-testid="new-bug-title" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs mb-1 block opacity-60">Description</Label>
              <Textarea value={newBug.description} onChange={(e) => setNewBug(b => ({ ...b, description: e.target.value }))} placeholder="Detailed description..." className="text-sm bg-transparent border-[var(--surface-border)] min-h-[60px]" data-testid="new-bug-desc" />
            </div>
            <div>
              <Label className="text-xs mb-1 block opacity-60">Severity</Label>
              <Select value={newBug.severity} onValueChange={(v) => setNewBug(b => ({ ...b, severity: v }))}>
                <SelectTrigger className="h-8 text-xs bg-transparent border-[var(--surface-border)]" data-testid="new-bug-severity"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--surface-bg)] border-[var(--surface-border)]">
                  <SelectItem value="low" className="text-xs">Low</SelectItem>
                  <SelectItem value="medium" className="text-xs">Medium</SelectItem>
                  <SelectItem value="high" className="text-xs">High</SelectItem>
                  <SelectItem value="critical" className="text-xs">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs mb-1 block opacity-60">Assignee</Label>
              <Select value={newBug.assignee_id || 'unassigned'} onValueChange={(v) => setNewBug(b => ({ ...b, assignee_id: v === 'unassigned' ? '' : v }))}>
                <SelectTrigger className="h-8 text-xs bg-transparent border-[var(--surface-border)]" data-testid="new-bug-assignee"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent className="bg-[var(--surface-bg)] border-[var(--surface-border)]">
                  <SelectItem value="unassigned" className="text-xs">Unassigned</SelectItem>
                  {members.map(m => <SelectItem key={m.user_id} value={m.user_id} className="text-xs">{m.name || m.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs mb-1 block opacity-60">Steps to Reproduce</Label>
              <Textarea value={newBug.steps_to_reproduce} onChange={(e) => setNewBug(b => ({ ...b, steps_to_reproduce: e.target.value }))} placeholder="1. Go to...\n2. Click on...\n3. Observe..." className="text-sm bg-transparent border-[var(--surface-border)] min-h-[60px]" data-testid="new-bug-steps" />
            </div>
            <div>
              <Label className="text-xs mb-1 block opacity-60">Expected Behavior</Label>
              <Textarea value={newBug.expected_behavior} onChange={(e) => setNewBug(b => ({ ...b, expected_behavior: e.target.value }))} placeholder="What should happen" className="text-sm bg-transparent border-[var(--surface-border)] min-h-[40px]" data-testid="new-bug-expected" />
            </div>
            <div>
              <Label className="text-xs mb-1 block opacity-60">Actual Behavior</Label>
              <Textarea value={newBug.actual_behavior} onChange={(e) => setNewBug(b => ({ ...b, actual_behavior: e.target.value }))} placeholder="What actually happens" className="text-sm bg-transparent border-[var(--surface-border)] min-h-[40px]" data-testid="new-bug-actual" />
            </div>
            <div className="col-span-2 flex gap-2">
              <Button size="sm" className="h-8 text-xs bg-red-500 hover:bg-red-600 text-white" onClick={handleCreateBug} data-testid="create-bug-btn">
                <Bug className="w-4 h-4 mr-1" /> Report Bug
              </Button>
              <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setShowAddForm(false)} data-testid="cancel-bug-btn">
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bug List */}
      <ScrollArea className="flex-1">
        <div className="p-3 space-y-2">
          {filteredBugs.map(bug => {
            const isExpanded = expandedBug === bug.id;
            const SeverityIcon = severityConfig[bug.severity]?.icon || Clock;
            return (
              <div key={bug.id} className="bg-[var(--surface-card)] rounded-lg border border-[var(--surface-border)] overflow-hidden" data-testid={`bug-card-${bug.id}`}>
                <div className="p-3 flex items-start gap-3 cursor-pointer hover:bg-[var(--surface-bg)]/50" onClick={() => setExpandedBug(isExpanded ? null : bug.id)}>
                  <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${severityConfig[bug.severity]?.color.split(' ')[0]}`}>
                    <SeverityIcon className={`w-4 h-4 ${severityConfig[bug.severity]?.color.split(' ')[1]}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {isExpanded ? <ChevronDown className="w-4 h-4 opacity-40" /> : <ChevronRight className="w-4 h-4 opacity-40" />}
                      <p className="text-sm font-medium truncate">{bug.title}</p>
                    </div>
                    {bug.description && !isExpanded && <p className="text-xs opacity-50 line-clamp-1 mt-0.5 ml-5">{bug.description}</p>}
                    <div className="flex flex-wrap gap-1.5 mt-2 ml-5">
                      <Badge className={`text-xs h-4 ${severityConfig[bug.severity]?.color}`}>{bug.severity}</Badge>
                      <Badge className={`text-xs h-4 ${statusConfig[bug.status]?.color}`}>{statusConfig[bug.status]?.label}</Badge>
                      <div className="flex items-center gap-1 text-xs opacity-50 ml-2">
                        <User className="w-4 h-4" /> {getMemberName(bug.assignee_id)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Select value={bug.status} onValueChange={(v) => handleUpdateBug(bug.id, { status: v })} onClick={(e) => e.stopPropagation()}>
                      <SelectTrigger className="h-7 text-xs w-24 bg-transparent border-[var(--surface-border)]"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[var(--surface-bg)] border-[var(--surface-border)]">
                        {Object.entries(statusConfig).map(([k, v]) => <SelectItem key={k} value={k} className="text-xs">{v.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button variant="ghost" size="icon" className="h-7 w-6 opacity-30 hover:opacity-100 hover:text-red-400" onClick={(e) => { e.stopPropagation(); handleDeleteBug(bug.id); }} data-testid={`delete-bug-${bug.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                {isExpanded && (
                  <div className="px-3 pb-3 border-t border-[var(--surface-border)] bg-[var(--surface-bg)]/30">
                    <div className="grid grid-cols-2 gap-4 mt-3">
                      {bug.description && (
                        <div className="col-span-2">
                          <p className="text-xs font-medium opacity-40 uppercase tracking-wider mb-1">Description</p>
                          <p className="text-xs opacity-70">{bug.description}</p>
                        </div>
                      )}
                      {bug.steps_to_reproduce && (
                        <div className="col-span-2">
                          <p className="text-xs font-medium opacity-40 uppercase tracking-wider mb-1">Steps to Reproduce</p>
                          <pre className="text-xs opacity-70 whitespace-pre-wrap font-sans">{bug.steps_to_reproduce}</pre>
                        </div>
                      )}
                      {bug.expected_behavior && (
                        <div>
                          <p className="text-xs font-medium opacity-40 uppercase tracking-wider mb-1">Expected</p>
                          <p className="text-xs opacity-70">{bug.expected_behavior}</p>
                        </div>
                      )}
                      {bug.actual_behavior && (
                        <div>
                          <p className="text-xs font-medium opacity-40 uppercase tracking-wider mb-1">Actual</p>
                          <p className="text-xs opacity-70">{bug.actual_behavior}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {filteredBugs.length === 0 && (
            <div className="py-16 text-center">
              <Bug className="w-10 h-10 opacity-10 mx-auto mb-2" />
              <p className="opacity-30 text-sm">No bugs reported</p>
              <p className="opacity-20 text-xs mt-1">Click "Report Bug" to log an issue</p>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
